import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTornaIndietro } from '../common/navigazione';
import { getCorriere, updateCorriere, getBanche } from '../services/api';
import { Form, Button, Container, Row, Col, Card, Spinner, Alert } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import RegoleSpedizioneManager from '../components/RegoleSpedizioneManager';


// Stessa formula del backend (Corriere.costo_netto_effettivo): costo ivato meno sconto ricariche, senza IVA se detraibile
const costoNettoStimato = (f) => {
  const ivato = parseFloat(f.costo_ivato);
  if (!ivato) return parseFloat(f.costo) ? Number(f.costo).toFixed(2) : '—';
  const scontato = ivato * (1 - (parseFloat(f.sconto_ricarica_pct) || 0) / 100);
  return (f.iva_detraibile === false ? scontato : scontato / 1.22).toFixed(2);
};

function CorrieriEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const tornaIndietro = useTornaIndietro('/corrieri');
  const [formData, setFormData] = useState({
    denominazione: '',
    telefono: '',
    cellulare: '',
    tipo_spedizione: '',
    costo: '',
    costo_ivato: null,
    sconto_ricarica_pct: 0,
    iva_detraibile: true,
    referente: '',
    mirakl_carrier_code: '',
    banca_pagamento_id: null,
    paccofacile_service_id: '',
  });
  const [banche, setBanche] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [corriereRes, bancheRes] = await Promise.all([
          getCorriere(id),
          getBanche(0, 1000) // Carica tutte le banche
        ]);
        setFormData(corriereRes.data);
        setBanche(bancheRes.data.banche);
      } catch (err) {
        setError("Errore nel caricamento dei dati.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    let processedValue = value;

    if (type === 'number') {
      processedValue = value === '' ? null : parseFloat(value);
    } else if (value === '') {
      processedValue = null;
    }

    setFormData({
        ...formData,
        [name]: processedValue
    });
  };

  const handleBancaChange = (selected) => {
    const bancaId = selected.length > 0 ? selected[0].id : null;
    setFormData({
        ...formData,
        banca_pagamento_id: bancaId
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateCorriere(id, formData);
      alert("Corriere aggiornato con successo!");
      navigate('/corrieri');
    } catch (error) {
      console.error("Errore nell'aggiornamento del corriere:", error.response || error);
      alert("Errore nell'aggiornamento del corriere.");
    }
  };

  if (loading) {
    return <div className="text-center"><Spinner animation="border" /></div>;
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  return (
    <Container>
        <Row className="align-items-center mb-3">
            <Col>
                <h2>Modifica Corriere</h2>
            </Col>
            <Col xs="auto" className="ms-auto">
                <Button variant="secondary" onClick={tornaIndietro} className="me-2">
                    Torna alla Lista
                </Button>
                <Button variant="primary" type="submit" form="corriere-edit-form">
                    Salva Modifiche
                </Button>
            </Col>
        </Row>
      <Form id="corriere-edit-form" onSubmit={handleSubmit}>
        <Card className="mb-4">
          <Card.Header>ANAGRAFICA</Card.Header>
          <Card.Body>
            <Form.Group className="mb-3" controlId="formDenominazione">
              <Form.Label>Denominazione</Form.Label>
              <Form.Control type="text" name="denominazione" value={formData.denominazione || ''} onChange={handleChange} required />
            </Form.Group>

            <Form.Group className="mb-3" controlId="formReferente">
              <Form.Label>Referente</Form.Label>
              <Form.Control type="text" name="referente" value={formData.referente || ''} onChange={handleChange} />
            </Form.Group>

            <Row>
              <Col md={6}><Form.Group className="mb-3" controlId="formTelefono"><Form.Label>Telefono</Form.Label><Form.Control type="tel" name="telefono" value={formData.telefono || ''} onChange={handleChange} placeholder="es. 06 / 1234567" /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3" controlId="formCellulare"><Form.Label>Cellulare</Form.Label><Form.Control type="tel" name="cellulare" value={formData.cellulare || ''} onChange={handleChange} placeholder="es. 333 / 1234567" /></Form.Group></Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header>SPEDIZIONE E PAGAMENTO</Card.Header>
          <Card.Body>
            <Row>
                <Col md={6}><Form.Group className="mb-3" controlId="formTipoSpedizione"><Form.Label>Tipo Spedizione</Form.Label><Form.Control type="text" name="tipo_spedizione" value={formData.tipo_spedizione || ''} onChange={handleChange} /></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3" controlId="formCosto"><Form.Label>Costo (storico, usato solo se il costo reale qui sotto è vuoto)</Form.Label><Form.Control type="number" step="0.01" name="costo" value={formData.costo || ''} onChange={handleChange} /></Form.Group></Col>
                <Col md={4}><Form.Group className="mb-3" controlId="formCostoIvato"><Form.Label>Costo reale per spedizione (IVA compresa)</Form.Label><Form.Control type="number" step="0.01" name="costo_ivato" value={formData.costo_ivato ?? ''} onChange={handleChange} placeholder="es. 5.67" /></Form.Group></Col>
                <Col md={4}><Form.Group className="mb-3" controlId="formScontoRicarica"><Form.Label>Sconto sulle ricariche (%)</Form.Label><Form.Control type="number" step="0.1" name="sconto_ricarica_pct" value={formData.sconto_ricarica_pct ?? ''} onChange={handleChange} placeholder="es. 4" /></Form.Group></Col>
                <Col md={4}><Form.Group className="mb-3" controlId="formIvaDetraibile"><Form.Label>IVA sulla spedizione</Form.Label><Form.Check type="switch" name="iva_detraibile" label="Detraibile" checked={formData.iva_detraibile !== false} onChange={(e) => setFormData({ ...formData, iva_detraibile: e.target.checked })} /><Form.Text className="text-muted">Costo netto usato nei margini: {costoNettoStimato(formData)} €</Form.Text></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3" controlId="formMiraklCarrierCode"><Form.Label>Codice Corriere Mirakl</Form.Label><Form.Control type="text" name="mirakl_carrier_code" value={formData.mirakl_carrier_code || ''} onChange={handleChange} /></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3" controlId="formPaccofacileServiceId"><Form.Label>Service ID PaccoFacile</Form.Label><Form.Control type="text" name="paccofacile_service_id" value={formData.paccofacile_service_id || ''} onChange={handleChange} placeholder="es. 92 (Poste Crono), 38 (BRT Express)" /></Form.Group></Col>
                <Col md={6}>
                    <Form.Group className="mb-3" controlId="formBancaPagamento">
                        <Form.Label>Banca di Pagamento</Form.Label>
                        <Typeahead
                            id="banca-pagamento-typeahead"
                            labelKey="denominazione"
                            options={banche}
                            placeholder="Seleziona una banca..."
                            onChange={handleBancaChange}
                            selected={banche.filter(b => b.id === formData.banca_pagamento_id)}
                            clearButton
                        />
                    </Form.Group>
                </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header>Regole di Spedizione</Card.Header>
          <Card.Body>
            <RegoleSpedizioneManager corriereId={id} />
          </Card.Body>
        </Card>


      </Form>
    </Container>
  );
}

export default CorrieriEdit;